/**
 * DEALSHAQ TOOLTIP DATA
 * Phase 3: UI Tooltips System
 * 
 * Maps UI components to documentation topics for contextual help.
 * Tooltips are short, clear, and actionable.
 * 
 * LANGUAGE POLICY:
 * - Retailer/Admin/Charity: English-only (no i18n keys)
 * - Consumer: Supports localization via i18n keys
 */

// =============================================================================
// RETAILER TOOLTIPS (English-only)
// =============================================================================
export const RETAILER_TOOLTIPS = {
  // Scanner Tooltips
  scanner: {
    main: {
      id: "retailer-scanner-main",
      title: "Barcode Scanner",
      content: "Scan product barcodes to quickly add items to your posting queue. Supports 7 different scan modes.",
      docLink: "/retailer/help?doc=retailer-barcode-scanning&section=scan-modes",
      intent: "docs.how_to",
      contextHint: "barcode scanning modes"
    },
    singleScan: {
      id: "retailer-scanner-single",
      title: "Single Scan Mode",
      content: "Scan one barcode at a time. Best for individual items or when learning the system.",
      docLink: "/retailer/help?doc=retailer-barcode-scanning&section=scan-modes",
      intent: "docs.how_to",
      contextHint: "single scan mode"
    },
    continuousScan: {
      id: "retailer-scanner-continuous",
      title: "Continuous Scan Mode",
      content: "Rapid-fire scanning without pausing. Point at barcodes continuously for high-volume posting.",
      docLink: "/retailer/help?doc=retailer-barcode-scanning&section=scan-modes",
      intent: "docs.how_to",
      contextHint: "continuous scanning"
    },
    ocrCapture: {
      id: "retailer-scanner-ocr",
      title: "OCR Price Capture",
      content: "Automatically extract prices and expiration dates from labels. Works best with good lighting.",
      docLink: "/retailer/help?doc=retailer-barcode-scanning&section=ocr-extraction",
      intent: "docs.how_to",
      contextHint: "OCR price extraction"
    },
    queue: {
      id: "retailer-scanner-queue",
      title: "Scan Queue",
      content: "Items are held here before posting. Minimum 2 items required. Review and edit before submitting.",
      docLink: "/retailer/help?doc=retailer-barcode-scanning&section=queue-system",
      intent: "docs.how_to",
      contextHint: "scan queue posting"
    }
  },

  // Order Management Tooltips
  orders: {
    main: {
      id: "retailer-orders-main",
      title: "Order Management",
      content: "View and manage all incoming orders. Acknowledge within 5 minutes to avoid auto-cancel.",
      docLink: "/retailer/help?doc=retailer-order-management&section=order-states",
      intent: "docs.how_to",
      contextHint: "order management"
    },
    acknowledge: {
      id: "retailer-orders-ack",
      title: "Acknowledge Order",
      content: "Confirm you received the order and commit to a ready-by time. Must be done within 5 minutes.",
      docLink: "/retailer/help?doc=retailer-order-management&section=acknowledging-orders",
      intent: "docs.how_to",
      contextHint: "acknowledging orders"
    },
    markReady: {
      id: "retailer-orders-ready",
      title: "Mark Ready",
      content: "Indicate the order is prepared. This triggers payment capture and notifies the customer.",
      docLink: "/retailer/help?doc=retailer-order-management&section=marking-ready",
      intent: "docs.how_to",
      contextHint: "marking orders ready"
    },
    dispatch: {
      id: "retailer-orders-dispatch",
      title: "Dispatch Order",
      content: "Send the order for delivery via DoorDash. Customer receives tracking information.",
      docLink: "/retailer/help?doc=retailer-order-management&section=dispatching-orders",
      intent: "docs.how_to",
      contextHint: "dispatching delivery"
    },
    timeout: {
      id: "retailer-orders-timeout",
      title: "Order Timeouts",
      content: "Orders auto-resend after 5 min without acknowledgment. Auto-cancel after 10 min total.",
      docLink: "/retailer/help?doc=retailer-order-management&section=timeouts",
      intent: "docs.error_help",
      contextHint: "order timeout rules"
    }
  },

  // Item Posting Tooltips
  posting: {
    main: {
      id: "retailer-posting-main",
      title: "Post Items",
      content: "Create deals from scanned items. Set discount level, quantity, and expiration.",
      docLink: "/retailer/help?doc=retailer-item-posting&section=posting-flow",
      intent: "docs.how_to",
      contextHint: "posting deals"
    },
    dealLevel: {
      id: "retailer-posting-deal",
      title: "Deal Levels",
      content: "Sizzling (50% off), Hot (60% off), Sizzling Hot (75% off). Higher discounts for items closer to expiration.",
      docLink: "/retailer/help?doc=retailer-item-posting&section=discount-levels",
      intent: "docs.definition",
      contextHint: "deal discount levels"
    },
    expiration: {
      id: "retailer-posting-expiry",
      title: "Expiration Date",
      content: "Required for all items. Items past expiration are automatically blocked from posting.",
      docLink: "/retailer/help?doc=retailer-item-posting&section=expiration-rules",
      intent: "docs.how_to",
      contextHint: "expiration date rules"
    },
    quantity: {
      id: "retailer-posting-qty",
      title: "Quantity",
      content: "Set available units. Stock updates in real-time. Low stock (≤4) triggers alerts.",
      docLink: "/retailer/help?doc=retailer-item-posting&section=quantity-rules",
      intent: "docs.how_to",
      contextHint: "quantity settings"
    }
  },

  // Onboarding Tooltips
  onboarding: {
    main: {
      id: "retailer-onboarding-main",
      title: "Store Setup",
      content: "Complete the 7-step wizard to go live. Includes Stripe Connect for payments.",
      docLink: "/retailer/help?doc=retailer-onboarding&section=wizard-overview",
      intent: "docs.how_to",
      contextHint: "store onboarding"
    },
    stripe: {
      id: "retailer-onboarding-stripe",
      title: "Stripe Connect",
      content: "Link your bank account to receive automatic weekly payouts from completed orders.",
      docLink: "/retailer/help?doc=retailer-onboarding&section=stripe-integration",
      intent: "docs.how_to",
      contextHint: "Stripe Connect setup"
    },
    delivery: {
      id: "retailer-onboarding-delivery",
      title: "Delivery Settings",
      content: "Configure delivery radius, prep time, and pickup instructions.",
      docLink: "/retailer/help?doc=retailer-onboarding&section=delivery-config",
      intent: "docs.how_to",
      contextHint: "delivery configuration"
    }
  },

  // Settings Tooltips
  settings: {
    main: {
      id: "retailer-settings-main",
      title: "Store Settings",
      content: "Manage your store profile, notifications, delivery options, and team access.",
      docLink: "/retailer/help?doc=retailer-settings&section=store-profile",
      intent: "docs.topic_lookup",
      contextHint: "store settings"
    },
    notifications: {
      id: "retailer-settings-notif",
      title: "Notifications",
      content: "Configure how you receive order alerts. Keep push notifications ON for timely acknowledgment.",
      docLink: "/retailer/help?doc=retailer-settings&section=notification-settings",
      intent: "docs.how_to",
      contextHint: "notification settings"
    },
    prepTime: {
      id: "retailer-settings-prep",
      title: "Prep Time",
      content: "Average time to prepare orders. Affects ready-by time calculations shown to customers.",
      docLink: "/retailer/help?doc=retailer-settings&section=delivery-settings",
      intent: "docs.definition",
      contextHint: "prep time settings"
    }
  },

  // Dashboard Tooltips
  dashboard: {
    main: {
      id: "retailer-dashboard-main",
      title: "Dashboard",
      content: "Your store performance at a glance. Active items, pending orders, and today's revenue.",
      docLink: "/retailer/help?doc=retailer-dashboard-analytics&section=dashboard-overview",
      intent: "docs.topic_lookup",
      contextHint: "retailer dashboard"
    },
    kpis: {
      id: "retailer-dashboard-kpis",
      title: "Performance KPIs",
      content: "Track acknowledgment time, fulfillment rate, and scan efficiency. Affects your visibility.",
      docLink: "/retailer/help?doc=retailer-kpis&section=acknowledgment-time",
      intent: "docs.definition",
      contextHint: "retailer KPIs"
    }
  }
};

// =============================================================================
// CONSUMER TOOLTIPS (Supports i18n)
// =============================================================================
export const CONSUMER_TOOLTIPS = {
  // Cart Tooltips
  cart: {
    main: {
      id: "consumer-cart-main",
      titleKey: "tooltips.cart.title",
      titleFallback: "Shopping Cart",
      contentKey: "tooltips.cart.content",
      contentFallback: "Items from one or multiple stores. Review before checkout.",
      docLink: "/consumer/help?doc=consumer-shopping-checkout&section=cart-management",
      intent: "docs.topic_lookup",
      contextHint: "shopping cart"
    },
    multiStore: {
      id: "consumer-cart-multistore",
      titleKey: "tooltips.cart.multistore.title",
      titleFallback: "Multi-Store Cart",
      contentKey: "tooltips.cart.multistore.content",
      contentFallback: "You have items from multiple stores. Each store ships separately.",
      docLink: "/consumer/help?doc=consumer-store-discovery&section=multi-store-carts",
      intent: "docs.how_to",
      contextHint: "multi-store shopping"
    },
    savings: {
      id: "consumer-cart-savings",
      titleKey: "tooltips.cart.savings.title",
      titleFallback: "Your Savings",
      contentKey: "tooltips.cart.savings.content",
      contentFallback: "Total discount compared to regular prices. DealShaq saves you 50-75%.",
      docLink: "/consumer/help?doc=consumer-shopping-checkout&section=savings-tracking",
      intent: "docs.definition",
      contextHint: "savings calculation"
    }
  },

  // Store Switcher Tooltips
  storeSwitcher: {
    main: {
      id: "consumer-store-main",
      titleKey: "tooltips.store.title",
      titleFallback: "Store Selector",
      contentKey: "tooltips.store.content",
      contentFallback: "Browse deals from different stores. Set preferred stores in settings.",
      docLink: "/consumer/help?doc=consumer-store-discovery&section=switching-stores",
      intent: "docs.how_to",
      contextHint: "store selection"
    },
    preferred: {
      id: "consumer-store-preferred",
      titleKey: "tooltips.store.preferred.title",
      titleFallback: "Preferred Stores",
      contentKey: "tooltips.store.preferred.content",
      contentFallback: "Your favorite stores. AI Agent searches these first when sourcing your list.",
      docLink: "/consumer/help?doc=consumer-store-discovery&section=preferred-stores",
      intent: "docs.how_to",
      contextHint: "preferred stores"
    },
    nearby: {
      id: "consumer-store-nearby",
      titleKey: "tooltips.store.nearby.title",
      titleFallback: "Nearby Stores",
      contentKey: "tooltips.store.nearby.content",
      contentFallback: "Stores within your delivery radius. Radius can be adjusted in settings.",
      docLink: "/consumer/help?doc=consumer-store-discovery&section=nearby-stores",
      intent: "docs.topic_lookup",
      contextHint: "nearby stores"
    }
  },

  // Agent Tooltips
  agent: {
    main: {
      id: "consumer-agent-main",
      titleKey: "tooltips.agent.title",
      titleFallback: "DealShaq Shopping Assistant (DSA)",
      contentKey: "tooltips.agent.content",
      contentFallback: "AI shopping assistant. Ask for recipes, find deals, or submit your grocery list.",
      docLink: "/consumer/help?doc=consumer-dealshaq-agent&section=agent-overview",
      intent: "docs.topic_lookup",
      contextHint: "AI shopping assistant"
    },
    shoppingList: {
      id: "consumer-agent-list",
      titleKey: "tooltips.agent.list.title",
      titleFallback: "Shopping List",
      contentKey: "tooltips.agent.list.content",
      contentFallback: "Give the agent your grocery list. It finds the sizzling hot deals across stores.",
      docLink: "/consumer/help?doc=consumer-dealshaq-agent&section=shopping-list-sourcing",
      intent: "docs.how_to",
      contextHint: "shopping list sourcing"
    },
    upload: {
      id: "consumer-agent-upload",
      titleKey: "tooltips.agent.upload.title",
      titleFallback: "Upload List",
      contentKey: "tooltips.agent.upload.content",
      contentFallback: "Upload a text file, CSV, or photo of your handwritten list.",
      docLink: "/consumer/help?doc=consumer-dealshaq-agent&section=shopping-list-sourcing",
      intent: "docs.how_to",
      contextHint: "upload shopping list"
    },
    memory: {
      id: "consumer-agent-memory",
      titleKey: "tooltips.agent.memory.title",
      titleFallback: "Agent Memory",
      contentKey: "tooltips.agent.memory.content",
      contentFallback: "The agent remembers your preferences. Clear memory anytime in settings.",
      docLink: "/consumer/help?doc=consumer-dealshaq-agent&section=memory-system",
      intent: "docs.definition",
      contextHint: "agent memory"
    }
  },

  // Checkout Tooltips
  checkout: {
    main: {
      id: "consumer-checkout-main",
      titleKey: "tooltips.checkout.title",
      titleFallback: "Checkout",
      contentKey: "tooltips.checkout.content",
      contentFallback: "Review your order, select delivery or pickup, and complete payment.",
      docLink: "/consumer/help?doc=consumer-shopping-checkout&section=checkout-process",
      intent: "docs.how_to",
      contextHint: "checkout process"
    },
    delivery: {
      id: "consumer-checkout-delivery",
      titleKey: "tooltips.checkout.delivery.title",
      titleFallback: "Delivery Options",
      contentKey: "tooltips.checkout.delivery.content",
      contentFallback: "Choose delivery or pickup. Delivery via DoorDash with real-time tracking.",
      docLink: "/consumer/help?doc=consumer-shopping-checkout&section=delivery-options",
      intent: "docs.how_to",
      contextHint: "delivery options"
    },
    payment: {
      id: "consumer-checkout-payment",
      titleKey: "tooltips.checkout.payment.title",
      titleFallback: "Payment",
      contentKey: "tooltips.checkout.payment.content",
      contentFallback: "Secure payment via Stripe. Card is authorized but not charged until order is ready.",
      docLink: "/consumer/help?doc=consumer-shopping-checkout&section=payment-flow",
      intent: "docs.how_to",
      contextHint: "payment process"
    },
    substitutions: {
      id: "consumer-checkout-subs",
      titleKey: "tooltips.checkout.subs.title",
      titleFallback: "Substitutions",
      contentKey: "tooltips.checkout.subs.content",
      contentFallback: "Allow or disallow item substitutions if something is out of stock.",
      docLink: "/consumer/help?doc=consumer-shopping-checkout&section=substitutions",
      intent: "docs.how_to",
      contextHint: "substitution preferences"
    },
    roundup: {
      id: "consumer-checkout-roundup",
      titleKey: "tooltips.checkout.roundup.title",
      titleFallback: "Round-Up Donation",
      contentKey: "tooltips.checkout.roundup.content",
      contentFallback: "Round up your total to donate the difference to food security charities.",
      docLink: "/consumer/help?doc=consumer-impact&section=charity-roundup",
      intent: "docs.how_to",
      contextHint: "charity round-up"
    }
  },

  // Orders Tooltips
  orders: {
    main: {
      id: "consumer-orders-main",
      titleKey: "tooltips.orders.title",
      titleFallback: "Your Orders",
      contentKey: "tooltips.orders.content",
      contentFallback: "Track active orders and view order history.",
      docLink: "/consumer/help?doc=consumer-orders-tracking&section=order-tracking",
      intent: "docs.topic_lookup",
      contextHint: "order tracking"
    },
    status: {
      id: "consumer-orders-status",
      titleKey: "tooltips.orders.status.title",
      titleFallback: "Order Status",
      contentKey: "tooltips.orders.status.content",
      contentFallback: "Pending → Preparing → Ready → Dispatched → Delivered",
      docLink: "/consumer/help?doc=consumer-orders-tracking&section=order-states",
      intent: "docs.definition",
      contextHint: "order status meanings"
    }
  }
};

// =============================================================================
// ADMIN TOOLTIPS (English-only)
// =============================================================================
export const ADMIN_TOOLTIPS = {
  // Approvals Tooltips
  approvals: {
    retailer: {
      id: "admin-approvals-retailer",
      title: "Retailer Approvals",
      content: "Review and approve new retailer applications. Verify documents and Stripe status.",
      docLink: "/admin/help?doc=admin-retailer-approvals&section=approval-queue",
      intent: "docs.how_to",
      contextHint: "retailer approval process"
    },
    charity: {
      id: "admin-approvals-charity",
      title: "Charity Approvals",
      content: "Verify 501(c)(3) status and approve charity partners for donation program.",
      docLink: "/admin/help?doc=admin-charity-approvals&section=charity-requirements",
      intent: "docs.how_to",
      contextHint: "charity approval process"
    }
  },

  // Incidents Tooltips
  incidents: {
    main: {
      id: "admin-incidents-main",
      title: "Incident Dashboard",
      content: "Monitor and resolve platform incidents. Critical issues require immediate attention.",
      docLink: "/admin/help?doc=admin-incident-management&section=incident-overview",
      intent: "docs.topic_lookup",
      contextHint: "incident management"
    },
    severity: {
      id: "admin-incidents-severity",
      title: "Severity Levels",
      content: "Critical (<30min), High (<1hr), Medium (<24hr), Low (<48hr) resolution targets.",
      docLink: "/admin/help?doc=admin-incident-management&section=incident-overview",
      intent: "docs.definition",
      contextHint: "incident severity levels"
    },
    escalation: {
      id: "admin-incidents-escalate",
      title: "Escalation",
      content: "Route incidents to specialized teams: Finance, Security, Legal, Engineering.",
      docLink: "/admin/help?doc=admin-incident-management&section=escalation-procedures",
      intent: "docs.how_to",
      contextHint: "incident escalation"
    }
  },

  // Telemetry Tooltips
  telemetry: {
    main: {
      id: "admin-telemetry-main",
      title: "Agent Telemetry",
      content: "Monitor AI Agent performance: success rate, fallback rate, intent distribution.",
      docLink: "/admin/help?doc=admin-agent-telemetry&section=telemetry-overview",
      intent: "docs.topic_lookup",
      contextHint: "agent telemetry"
    },
    fallback: {
      id: "admin-telemetry-fallback",
      title: "Fallback Analysis",
      content: "Review conversations where the AI couldn't help. Use to improve prompts.",
      docLink: "/admin/help?doc=admin-agent-telemetry&section=fallback-analysis",
      intent: "docs.how_to",
      contextHint: "fallback analysis"
    }
  },

  // Platform Health Tooltips
  health: {
    main: {
      id: "admin-health-main",
      title: "Platform Health",
      content: "Real-time system status: API latency, error rates, order volume.",
      docLink: "/admin/help?doc=admin-platform-health&section=health-dashboard",
      intent: "docs.topic_lookup",
      contextHint: "platform health"
    }
  }
};

// =============================================================================
// CHARITY TOOLTIPS (English-only)
// =============================================================================
export const CHARITY_TOOLTIPS = {
  // Dashboard Tooltips
  dashboard: {
    main: {
      id: "charity-dashboard-main",
      title: "Donation Dashboard",
      content: "View Total Charitable Contributions, pending payouts, and contributing consumers.",
      docLink: "/charity/help?doc=charity-donation-dashboard&section=dashboard-overview",
      intent: "docs.topic_lookup",
      contextHint: "donation dashboard"
    },
    payout: {
      id: "charity-dashboard-payout",
      title: "Payout Schedule",
      content: "Monthly payouts on the 15th. Minimum $100 required. Bank details in settings.",
      docLink: "/charity/help?doc=charity-donation-dashboard&section=payout-schedule",
      intent: "docs.how_to",
      contextHint: "payout schedule"
    }
  },

  // Impact Tooltips
  impact: {
    main: {
      id: "charity-impact-main",
      title: "Impact Tracking",
      content: "Measure meals provided, families served, and food distributed.",
      docLink: "/charity/help?doc=charity-impact-metrics&section=impact-dashboard",
      intent: "docs.topic_lookup",
      contextHint: "impact tracking"
    },
    multipliers: {
      id: "charity-impact-mult",
      title: "Impact Multipliers",
      content: "Set custom multipliers ($ per meal) for accurate impact reporting.",
      docLink: "/charity/help?doc=charity-impact-metrics&section=impact-dashboard",
      intent: "docs.how_to",
      contextHint: "impact multipliers"
    }
  },

  // Reports Tooltips
  reports: {
    main: {
      id: "charity-reports-main",
      title: "Reports & Exports",
      content: "Generate financial reports, impact summaries, and tax documents.",
      docLink: "/charity/help?doc=charity-reports&section=available-reports",
      intent: "docs.topic_lookup",
      contextHint: "charity reports"
    }
  }
};

// =============================================================================
// SCREEN CONTEXT MAPPING
// Maps routes/screens to contextual help topics for the AI Agent
// =============================================================================
export const SCREEN_CONTEXT_MAP = {
  // Retailer Screens
  '/retailer/dashboard': {
    portal: 'retailer',
    primaryTopic: 'dashboard',
    contextHints: ['retailer dashboard', 'store performance', 'active deals', 'pending orders'],
    priorityIntents: ['docs.topic_lookup', 'docs.how_to'],
    relatedDocs: ['retailer-dashboard-analytics', 'retailer-kpis']
  },
  '/retailer/scanner': {
    portal: 'retailer',
    primaryTopic: 'scanner',
    contextHints: ['barcode scanning', 'OCR', 'scan modes', 'queue system'],
    priorityIntents: ['docs.how_to', 'docs.error_help'],
    relatedDocs: ['retailer-barcode-scanning']
  },
  '/retailer/orders': {
    portal: 'retailer',
    primaryTopic: 'orders',
    contextHints: ['order management', 'acknowledge orders', 'mark ready', 'dispatch', 'timeouts'],
    priorityIntents: ['docs.how_to', 'docs.error_help'],
    relatedDocs: ['retailer-order-management']
  },
  '/retailer/inventory': {
    portal: 'retailer',
    primaryTopic: 'posting',
    contextHints: ['inventory', 'active deals', 'deal levels', 'item posting'],
    priorityIntents: ['docs.how_to', 'docs.topic_lookup'],
    relatedDocs: ['retailer-item-posting']
  },
  '/retailer/settings': {
    portal: 'retailer',
    primaryTopic: 'settings',
    contextHints: ['store settings', 'notifications', 'delivery', 'team access'],
    priorityIntents: ['docs.how_to', 'docs.topic_lookup'],
    relatedDocs: ['retailer-settings']
  },
  '/retailer/onboarding': {
    portal: 'retailer',
    primaryTopic: 'onboarding',
    contextHints: ['store setup', 'Stripe Connect', 'onboarding wizard'],
    priorityIntents: ['docs.how_to'],
    relatedDocs: ['retailer-onboarding']
  },

  // Consumer Screens
  '/consumer/dashboard': {
    portal: 'consumer',
    primaryTopic: 'shopping',
    contextHints: ['browse deals', 'nearby stores', 'savings'],
    priorityIntents: ['docs.topic_lookup', 'shopping_list.create'],
    relatedDocs: ['consumer-shopping-checkout', 'consumer-store-discovery']
  },
  '/consumer/agent': {
    portal: 'consumer',
    primaryTopic: 'agent',
    contextHints: ['AI assistant', 'shopping list', 'recipes', 'meal planning', 'dietary preferences'],
    priorityIntents: ['shopping_list.create', 'shopping_list.sourcing', 'docs.how_to'],
    relatedDocs: ['consumer-dealshaq-agent']
  },
  '/consumer/cart': {
    portal: 'consumer',
    primaryTopic: 'cart',
    contextHints: ['shopping cart', 'multi-store', 'checkout', 'savings'],
    priorityIntents: ['docs.how_to', 'shopping_list.review'],
    relatedDocs: ['consumer-shopping-checkout']
  },
  '/consumer/checkout': {
    portal: 'consumer',
    primaryTopic: 'checkout',
    contextHints: ['checkout', 'payment', 'delivery', 'pickup', 'substitutions'],
    priorityIntents: ['docs.how_to', 'docs.error_help'],
    relatedDocs: ['consumer-shopping-checkout']
  },
  '/consumer/orders': {
    portal: 'consumer',
    primaryTopic: 'orders',
    contextHints: ['order tracking', 'order status', 'delivery tracking'],
    priorityIntents: ['docs.topic_lookup', 'docs.how_to'],
    relatedDocs: ['consumer-orders-tracking']
  },
  '/consumer/stores': {
    portal: 'consumer',
    primaryTopic: 'stores',
    contextHints: ['store discovery', 'nearby stores', 'preferred stores', 'store switcher'],
    priorityIntents: ['docs.how_to', 'docs.topic_lookup'],
    relatedDocs: ['consumer-store-discovery']
  },

  // Admin Screens
  '/admin/dashboard': {
    portal: 'admin',
    primaryTopic: 'dashboard',
    contextHints: ['admin dashboard', 'platform health', 'incidents'],
    priorityIntents: ['docs.topic_lookup'],
    relatedDocs: ['admin-platform-health', 'admin-incident-management']
  },
  '/admin/incidents': {
    portal: 'admin',
    primaryTopic: 'incidents',
    contextHints: ['incidents', 'resolution', 'escalation', 'severity'],
    priorityIntents: ['docs.how_to', 'docs.error_help'],
    relatedDocs: ['admin-incident-management']
  },
  '/admin/retailers': {
    portal: 'admin',
    primaryTopic: 'approvals',
    contextHints: ['retailer approvals', 'verification', 'onboarding'],
    priorityIntents: ['docs.how_to'],
    relatedDocs: ['admin-retailer-approvals']
  },
  '/admin/charities': {
    portal: 'admin',
    primaryTopic: 'approvals',
    contextHints: ['charity approvals', '501c3', 'verification'],
    priorityIntents: ['docs.how_to'],
    relatedDocs: ['admin-charity-approvals']
  },
  '/admin/telemetry': {
    portal: 'admin',
    primaryTopic: 'telemetry',
    contextHints: ['agent telemetry', 'fallback', 'performance'],
    priorityIntents: ['docs.topic_lookup', 'docs.how_to'],
    relatedDocs: ['admin-agent-telemetry']
  },

  // Charity Screens
  '/charity/dashboard': {
    portal: 'charity',
    primaryTopic: 'dashboard',
    contextHints: ['donations', 'payouts', 'impact'],
    priorityIntents: ['docs.topic_lookup'],
    relatedDocs: ['charity-donation-dashboard']
  },
  '/charity/impact': {
    portal: 'charity',
    primaryTopic: 'impact',
    contextHints: ['impact tracking', 'meals provided', 'reporting'],
    priorityIntents: ['docs.how_to', 'docs.topic_lookup'],
    relatedDocs: ['charity-impact-metrics']
  },
  '/charity/reports': {
    portal: 'charity',
    primaryTopic: 'reports',
    contextHints: ['reports', 'exports', 'tax documents'],
    priorityIntents: ['docs.how_to'],
    relatedDocs: ['charity-reports']
  }
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get tooltip for a specific component
 */
export const getTooltip = (portal, category, key) => {
  const tooltipSets = {
    retailer: RETAILER_TOOLTIPS,
    consumer: CONSUMER_TOOLTIPS,
    admin: ADMIN_TOOLTIPS,
    charity: CHARITY_TOOLTIPS
  };
  
  const set = tooltipSets[portal];
  if (!set || !set[category]) return null;
  return set[category][key] || set[category].main;
};

/**
 * Get screen context for AI Agent
 */
export const getScreenContext = (pathname) => {
  // Try exact match first
  if (SCREEN_CONTEXT_MAP[pathname]) {
    return SCREEN_CONTEXT_MAP[pathname];
  }
  
  // Try prefix match
  for (const [route, context] of Object.entries(SCREEN_CONTEXT_MAP)) {
    if (pathname.startsWith(route)) {
      return context;
    }
  }
  
  // Default context based on portal
  if (pathname.startsWith('/retailer')) {
    return { portal: 'retailer', primaryTopic: 'general', contextHints: ['retailer help'], priorityIntents: ['docs.topic_lookup'] };
  }
  if (pathname.startsWith('/consumer')) {
    return { portal: 'consumer', primaryTopic: 'general', contextHints: ['consumer help'], priorityIntents: ['docs.topic_lookup'] };
  }
  if (pathname.startsWith('/admin')) {
    return { portal: 'admin', primaryTopic: 'general', contextHints: ['admin help'], priorityIntents: ['docs.topic_lookup'] };
  }
  if (pathname.startsWith('/charity')) {
    return { portal: 'charity', primaryTopic: 'general', contextHints: ['charity help'], priorityIntents: ['docs.topic_lookup'] };
  }
  
  return { portal: 'consumer', primaryTopic: 'general', contextHints: ['help'], priorityIntents: ['docs.topic_lookup'] };
};

/**
 * Check if portal is English-only
 */
export const isEnglishOnlyPortal = (portal) => {
  return ['retailer', 'admin', 'charity'].includes(portal);
};

export default {
  RETAILER_TOOLTIPS,
  CONSUMER_TOOLTIPS,
  ADMIN_TOOLTIPS,
  CHARITY_TOOLTIPS,
  SCREEN_CONTEXT_MAP,
  getTooltip,
  getScreenContext,
  isEnglishOnlyPortal
};
